/**
 * `LexiunWordmark` — the composite brand wordmark (swirl glyph + the
 * "Lexiun" text) rendered in the top-left of the LexIDE header.
 *
 * Inlined from `public/lexiun-wordmark.svg` with the hard-coded
 * `#D4A052` (gold-500) fills swapped to `currentColor` so the
 * wordmark inherits color from the parent's `text-*` class — matches
 * the `LexiunMark` pattern. Lets the header use the same tint as the
 * `/home` greeting glyph (`text-coffee-900 dark:text-gold-400`)
 * instead of being permanently gold-500.
 *
 * Size is controlled via the standard className passthrough (`h-*`
 * + `w-auto` keeps the aspect ratio). The original SVG had a wide
 * set of unused gradient definitions; those were stripped because
 * the visible content used flat `#D4A052` fills only.
 */

import type { SVGAttributes } from "react";

interface LexiunWordmarkProps extends SVGAttributes<SVGSVGElement> {}

export function LexiunWordmark(props: LexiunWordmarkProps) {
  // Glyph removed 2026-05-28 — the swirl mark is now redundant on the
  // header (the ActivityBar carries its own LexiunMark for the /chat
  // nav item, and the /home greeting also has the big breathe-glyph).
  // Showing it a third time in the header was visual noise.
  // viewBox tightened to crop the empty space the glyph used to fill;
  // the text-group translate is reset to x=0 so the wordmark sits
  // flush at the SVG's left edge.
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="51.47 101.18 180.53 47.63"
      preserveAspectRatio="xMinYMid meet"
      fill="currentColor"
      aria-hidden
      {...props}
    >
      <g fill="currentColor" transform="translate(53.795,103.507)">
        <g transform="translate(0,3.013)">
          <g transform="scale(1)">
            <path d="M2.74 0L37.3 0 37.3-3.89 6.62-3.89 6.62-34.61 2.74-34.61ZM39.84-22.03L39.84-5.81C39.84-2.63 42.47 0 45.65 0L68.21 0 68.21-3.94 45.65-3.94C44.63-3.94 43.78-4.79 43.78-5.81L43.78-11.95 68.21-11.95 68.21-22.03C68.21-25.23 65.57-27.84 62.35-27.84L45.65-27.84C42.47-27.84 39.84-25.21 39.84-22.03ZM45.65-23.9L62.35-23.9C63.37-23.9 64.22-23.05 64.22-22.03L64.22-15.89 43.78-15.89 43.78-22.03C43.78-23.05 44.63-23.9 45.65-23.9ZM95.28-27.84L86.21-17.28 77.18-27.84 72-27.84 83.62-14.16 72 0 77.18 0 86.21-11.14 95.28 0 100.46 0 88.8-14.16 100.42-27.84ZM105.5 0L109.44 0 109.44-27.84 105.5-27.84ZM105.5-36.96L105.5-33.02 109.44-33.02 109.44-36.96ZM138.05-3.94L121.34-3.94C120.33-3.94 119.47-4.79 119.47-5.81L119.47-27.84 115.54-27.84 115.54-5.81C115.54-2.63 118.17 0 121.34 0L138.05 0C141.26 0 143.9-2.61 143.9-5.81L143.9-27.84 139.97-27.84 139.97-5.81C139.97-4.79 139.06-3.94 138.05-3.94ZM155.04-23.9L171.74-23.9C172.76-23.9 173.62-23.05 173.62-22.03L173.62 0 177.55 0 177.55-22.03C177.55-25.21 174.92-27.84 171.74-27.84L149.18-27.84 149.18 0 153.12 0 153.12-22.03C153.12-23.05 154.03-23.9 155.04-23.9Z" transform="translate(-2.74,36.96)" />
          </g>
        </g>
      </g>
    </svg>
  );
}
